import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { http } from '../services/http';
import { token } from '../services/token';

/**
 * 로그인 상태 — admin 직원 계정. GRK_project 와 같은 방식이다.
 * 토큰이 끝나면(401) http 가 `auth:expired` 를 쏘고, 여기서 로그인 화면으로 돌린다.
 */
export interface User { id: number; name: string; email: string; role: string; department: string | null; profileImageUrl: string | null }

interface Ctx { user: User | null; loading: boolean; login: (accessToken: string) => Promise<void>; logout: () => void }
const AuthContext = createContext<Ctx>({ user: null, loading: true, login: async () => {}, logout: () => {} });
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const check = useCallback(async () => {
    if (!token.get()) { setUser(null); setLoading(false); return; }
    try { setUser(await http.get<User>('/auth/me')); } catch { token.clear(); setUser(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { check(); }, [check]);
  useEffect(() => {
    const expired = () => setUser(null);
    window.addEventListener('auth:expired', expired);
    return () => window.removeEventListener('auth:expired', expired);
  }, []);

  const login = async (accessToken: string) => { token.set(accessToken); setLoading(true); await check(); };
  const logout = () => { token.clear(); setUser(null); };

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>;
};
